import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Stack,
  Text,
  VStack,
  Image,
  Icon,
  SimpleGrid,
} from "@chakra-ui/react";
import SEO from "../SEO";
import {
  FaCheckCircle,
  FaClipboardList,
  FaCalendarAlt,
  FaClock,
  FaCogs,
  FaRegCheckCircle,
  FaCalendarDay,
  FaQuestion,
  FaBriefcase,
  FaExclamationCircle,
} from "react-icons/fa";
import { mainColor } from "./mainPage/MainPage";

const GroupBookingLanding = () => {
  return (
    <>
      <SEO
        title="Csoportos események kezelése egyszerűen | Wobble"
        description="Fogadd a jelentkezéseket automatikusan a Wobble csoportos foglalás rendszerével. Ingyenes béta hozzáférés most!"
        ogTitle="Wobble - Időpontfoglaló rendszer, online megjelenés"
        ogDescription="Fogadd a jelentkezéseket automatikusan a Wobble csoportos foglalás rendszerével. Ingyenes béta hozzáférés most!"
        keywords="csoportos esemény, időpontfoglalás, online jelentkezés, wobble rendszer"
      />

      <Box
        bgGradient="linear(to-b, white, gray.50)"
        minH="100vh"
        py={{ base: 12, md: 20 }}
        px={4}
      >
        <Container maxW="6xl">
          <Flex direction="column" align="center" textAlign="center" mb={20}>
            <Heading
              as="h1"
              mb={8}
              fontSize={{ base: "3xl", md: "5xl" }}
              fontWeight="bold"
              bgGradient="linear(to-r, teal.300, blue.500)"
              bgClip="text"
            >
              Ne szervezz többet chaten - kezelj eseményt profin
            </Heading>
            <Text
              fontSize={{ base: "lg", md: "xl" }}
              color="gray.600"
              maxW="3xl"
              as="h3"
            >
              Jelentkezések kezelése, infók megosztása, nyilvántartás egy
              helyen. Neked csak meg kell tartani az eseményt - mi intézzük a
              többit.
            </Text>
            <Text
              fontSize={{ base: "xl", md: "3xl" }}
              color="gray.600"
              maxW="3xl"
              textColor={mainColor}
              as="h3"
              fontStyle="italic"
              fontWeight={600}
            >
              Wobble csoportos foglalás!
            </Text>
          </Flex>

          <SimpleGrid
            columns={{ base: 1, md: 2 }}
            spacing={12}
            alignItems="center"
            mb={24}
          >
            <VStack align="start" spacing={5}>
              <Heading fontSize="2xl">Ismerős helyzetek?</Heading>
              <Stack spacing={2} color="gray.600">
                <Text>
                  <Icon as={FaClipboardList} color="teal.400" mr={2} />{" "}
                  Táblázatokban követed, ki jön?
                </Text>
                <Text>
                  <Icon as={FaClock} color="teal.400" mr={2} /> Lemondás esetén
                  nincs kinek szólni?
                </Text>
                <Text>
                  <Icon as={FaCogs} color="teal.400" mr={2} /> Minden
                  információt kézzel küldözgetsz újra és újra?
                </Text>
                <Text>
                  <Icon as={FaBriefcase} color="teal.400" mr={2} /> Sok idő
                  adminisztrálni, ki, mikor, milyen feltételekkel foglalt?
                </Text>
                <Text>
                  <Icon as={FaQuestion} color="teal.400" mr={2} />
                  Ugyanazok a kérdések, ugyanazok a válaszok állandóan?
                </Text>
              </Stack>
              <Text fontWeight="medium" color="gray.700" pt={2}>
                A Wobble segít automatizálni a csoportos események szervezését -
                egyszerűen, átláthatóan.
              </Text>
              <Button
                as="a"
                href="https://app.wobble.hu/signup_page"
                colorScheme="teal"
                size="lg"
                rounded="full"
                px={8}
                py={6}
                fontWeight="bold"
                target="_blank"
                rel="noopener noreferrer"
                textDecoration="none"
                boxShadow="lg"
                _hover={{
                  transform: "scale(1.05)",
                  boxShadow: "xl",
                  textDecoration: "none",
                }}
              >
                Kipróbálom ingyen
              </Button>
            </VStack>
            <Image
              src="/assets/group-book.png"
              alt="Csoportos foglalas"
              maxW="100%"
            />
          </SimpleGrid>

          <Box
            bg="gray.100"
            p={{ base: 10, md: 14 }}
            rounded="2xl"
            shadow="md"
            w="full"
            maxW="4xl"
            textAlign="left"
            mx="auto"
            mb={16}
            transition="transform 0.3s ease"
            _hover={{ transform: "scale(1.02)" }}
          >
            <Heading fontSize={{ base: "2xl", md: "3xl" }} mb={4}>
              Miért most van itt az ideje digitalizálni?
            </Heading>
            <Stack spacing={4} fontSize="md">
              <Text>
                <Icon as={FaCalendarAlt} color="teal.500" mr={2} /> Az emberek
                online keresnek programokat és online is jelentkeznek - ha nem
                vagy ott, nem találnak meg.
              </Text>
              <Text>
                <Icon as={FaCalendarDay} color="teal.500" mr={2} /> Online
                foglalás előnye, hogy bárhonnan bármikor tudnak foglalni hozzád
                azonnal, várakozás nélkül.
              </Text>
              <Text>
                <Icon as={FaClock} color="teal.500" mr={2} /> Adminisztráció
                helyett koncentrálj az élményre, amit adsz.
              </Text>
              <Text>
                <Icon as={FaCheckCircle} color="teal.500" mr={2} /> A
                digitalizáció profibbá tesz - és több résztvevőt is hozhat.
              </Text>
              <Text>
                <Icon as={FaCogs} color="teal.500" mr={2} /> Plusz! A Wobble-ben
                extra opciókat is megadhatsz: helyszín, tréner, felszerelés stb.
              </Text>
              <Text>
                <Icon as={FaExclamationCircle} color="teal.500" mr={2} /> Nem
                mellesleg a csapból is a digitalizáció folyik, az emberek egyre
                inkább azt választják, ahol online lehet intézni mindent.
              </Text>
            </Stack>
          </Box>

          <Box
            p={{ base: 6, md: 10 }}
            w="full"
            maxW="3xl"
            textAlign="left"
            mx="auto"
            mb={16}
          >
            <Text fontSize="2xl" fontWeight="bold" mb={4}>
              Miért fogod szeretni?
            </Text>
            <Stack spacing={4} color="gray.700">
              <Flex align="center" gap={3}>
                <Icon as={FaRegCheckCircle} color="teal.500" /> Percek alatt
                létrehozható esemény
              </Flex>
              <Flex align="center" gap={3}>
                <Icon as={FaRegCheckCircle} color="teal.500" /> Egyedi,
                megosztható foglalási link
              </Flex>
              <Flex align="center" gap={3}>
                <Icon as={FaRegCheckCircle} color="teal.500" /> Választható
                opciók pl. egyedi eszközök, személyek, helyszín megadása
              </Flex>
              <Flex align="center" gap={3}>
                <Icon as={FaRegCheckCircle} color="teal.500" /> Plusz az egyedi
                kérdőív funkciónk is használhatod
              </Flex>
              <Flex align="center" gap={3}>
                <Icon as={FaRegCheckCircle} color="teal.500" /> Automatikus
                visszaigazolás, nyilvántartás
              </Flex>
            </Stack>
          </Box>

          <Box
            bgGradient="linear(to-r, blue.600, teal.400)"
            p={{ base: 10, md: 14 }}
            rounded="2xl"
            color="white"
            textAlign="center"
            boxShadow="xl"
            transition="transform 0.3s ease"
            _hover={{ transform: "scale(1.02)" }}
            mb={20}
          >
            <Text color="white" fontSize="2xl" fontWeight="bold" mb={3}>
              🚀 Csatlakozz - ingyenes 30 napos időszak!
            </Text>
            <Text mb={6} fontSize="lg" color="white">
              Nem tudod neked való-e? Kételkedsz abban, hogy tényleg ennyire
              egyszerű beállítani? Beszéljünk és megmutatom! Nincs kockázat -
              csak előny.
            </Text>
            <Stack
              direction={{ base: "column", sm: "row" }}
              spacing={4}
              justify="center"
            >
              <Button
                colorScheme="whiteAlpha"
                size="lg"
                rounded="xl"
                as="a"
                href="https://app.wobble.hu/signup_page"
                target="_blank"
                rel="noopener noreferrer"
                textDecoration="none"
                boxShadow="lg"
                _hover={{
                  bg: "whiteAlpha.800", 
                  color: "teal" ,
                  transform: "scale(1.05)",
                  boxShadow: "xl",
                  textDecoration: "none",
                }}
              >
                Ingyenes nekem való-e meetinget kérek
              </Button>
            </Stack>
          </Box>

          <Text
            fontSize="sm"
            color="gray.500"
            mt={10}
            maxW="2xl"
            textAlign="center"
            mx="auto"
        
          >
            A Wobble-t kisvállalkozók visszajelzései alapján fejlesztjük. Neked
            készül, nem multiknak. A célunk: időt és energiát spórolni a
            szervezésben.
          </Text>
        </Container>
      </Box>
    </>
  );
};

export default GroupBookingLanding;
